import type React from 'react';
import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Switch,
  Text,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import type { Incident, StatusPanelSettings } from 'types';
import { updateIncidentTags } from 'services';
import { useToast } from 'contexts';

interface IncidentTagsDialogProps {
  incident: Incident;
  settings: StatusPanelSettings;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTagsUpdated?: (incident: Incident) => void;
}

export const IncidentTagsDialog: React.FC<IncidentTagsDialogProps> = ({
  incident,
  settings,
  open,
  onOpenChange,
  onTagsUpdated,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [isCritical, setIsCritical] = useState<boolean>(incident.tags?.isCritical ?? false);
  const [nis2Categories, setNis2Categories] = useState<string[]>(incident.tags?.nis2Categories ?? []);
  const [saving, setSaving] = useState(false);

  // Sync local state when dialog opens
  const handleOpenChange = useCallback(
    (_: unknown, data: { open: boolean }) => {
      if (data.open) {
        setIsCritical(incident.tags?.isCritical ?? false);
        setNis2Categories(incident.tags?.nis2Categories ?? []);
      }
      onOpenChange(data.open);
    },
    [incident, onOpenChange],
  );

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const response = await updateIncidentTags(incident.incidentId, isCritical, nis2Categories);
      const updatedIncident = response.data;
      if (updatedIncident) {
        onTagsUpdated?.(updatedIncident);
      }
      showToast({
        title: t('statusPanel.tags.saveSuccess'),
        intent: 'success',
        timeout: 3000,
      });
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to update tags:', err);
      showToast({
        title: t('statusPanel.tags.saveError'),
        body: err instanceof Error ? err.message : undefined,
        intent: 'error',
        timeout: 8000,
      });
    } finally {
      setSaving(false);
    }
  };

  const nis2Options = settings.nis2Categories ?? [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('statusPanel.tags.editTitle')}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Text>{t('statusPanel.tags.isCritical')}</Text>
                <Switch
                  checked={isCritical}
                  onChange={(_, data) => setIsCritical(data.checked)}
                />
              </div>
              {nis2Options.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Text>{t('statusPanel.tags.nis2Categories')}</Text>
                  <Dropdown
                    multiselect
                    placeholder={t('statusPanel.tags.noNis2')}
                    selectedOptions={nis2Categories}
                    value={nis2Categories.join(', ')}
                    onOptionSelect={(_, data) => {
                      setNis2Categories(data.selectedOptions as string[]);
                    }}
                  >
                    {nis2Options.map((cat) => (
                      <Option key={cat} value={cat}>
                        {cat}
                      </Option>
                    ))}
                  </Dropdown>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              appearance="primary"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? t('common.loading') : t('statusPanel.tags.save')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
